import React from "react";
import { Helmet } from "react-helmet-async";

export default function DtfPrintNotSticking() {
  return (
    <>
      <Helmet>
        <title>DTF Print Not Sticking? Causes + Fixes That Actually Work</title>
        <meta
          name="description"
          content="Fix DTF print not sticking fast. Learn causes, quick fixes, and how to stop peeling and adhesion issues."
        />
      </Helmet>

      <div className="px-4 py-10 max-w-3xl mx-auto text-white">
        <h1 className="text-3xl font-bold mb-6 text-center">
          DTF Print Not Sticking? (Causes + Fixes That Actually Work)
        </h1>

        <p className="mb-6 text-lg text-gray-300">
          If your DTF print isn’t sticking to the shirt, you’re not alone. This
          is one of the most common issues in DTF printing—and it usually comes
          down to a few key mistakes.
        </p>

        <h2 className="text-2xl font-semibold mb-3 mt-8">Common Causes</h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300">
          <li>Incorrect heat press temperature</li>
          <li>Not enough or uneven pressure</li>
          <li>Under-cured adhesive powder</li>
          <li>Moisture in the garment</li>
          <li>Low-quality or incompatible film</li>
        </ul>

        <h2 className="text-2xl font-semibold mb-3 mt-8">Quick Fixes</h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300">
          <li>Increase press temp to 305–320°F</li>
          <li>Use medium to firm pressure</li>
          <li>Fully cure powder before pressing</li>
          <li>Pre-press garment for 5–10 seconds</li>
        </ul>

        <h2 className="text-2xl font-semibold mb-3 mt-8">
          Why This Keeps Happening
        </h2>
        <p className="text-gray-300">
          This is where most people get stuck. You can follow standard settings
          and still have prints fail. That’s because DTF isn’t one-size-fits-all—your
          film, humidity, powder, and setup all play a role.
        </p>

        <div className="mt-10 p-6 rounded-xl bg-[rgba(20,30,50,0.6)] border border-blue-500/20">
          <h2 className="text-xl font-semibold mb-3">
            👉 Want the Exact Fix for Your Setup?
          </h2>

          <p className="text-gray-300 mb-4">
            DTF Wizard walks you step-by-step through diagnosing the issue and
            tells you exactly what to adjust based on your setup.
          </p>

          <p className="text-gray-400 mb-4 italic">
            Fix one issue—and this already pays for itself.
          </p>

          <a
            href="https://dtfwizardapp.com"
            className="inline-block px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition text-white font-semibold"
          >
            Get Access to DTF Wizard
          </a>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-semibold mb-4">FAQ</h2>

          <div className="space-y-4 text-gray-300">
            <p>
              <strong>
                Why is my DTF print not sticking even at the right temperature?
              </strong>
              <br />
              Usually pressure or curing is slightly off. Temperature alone
              isn’t enough.
            </p>

            <p>
              <strong>Can humidity affect DTF adhesion?</strong>
              <br />
              Yes. Moisture can prevent proper bonding.
            </p>

            <p>
              <strong>Why does it stick at first but peel later?</strong>
              <br />
              This is often due to under-curing or low pressure during
              pressing.
            </p>
          </div>
        </div>

        <div className="mt-12 text-gray-400">
          <p className="mb-2 font-semibold">Related DTF Issues:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <a
                href="/fix/dtf-peeling-after-wash"
                className="text-blue-400 hover:underline"
              >
                DTF Peeling After Wash
              </a>
            </li>
            <li>
              <a
                href="/fix/dtf-powder-not-curing"
                className="text-blue-400 hover:underline"
              >
                DTF Powder Not Curing
              </a>
            </li>
            <li>
              <a
                href="/fix/dtf-white-ink-not-printing"
                className="text-blue-400 hover:underline"
              >
                DTF White Ink Not Printing
              </a>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}